import { _js } from '@ifixit/localize';
import { SuggestItems, SuggestDeviceGuides } from 'Shared/suggest';
import { Modal } from 'Shared/modal';
import { Auth } from 'Shared/auth';
import { RangeSlider } from 'Shared/range_slider';
import { Utils } from 'Shared/utils';
import { FormManager } from 'Shared/form_manager';
import { MediaTarget } from 'Shared/FrameModules/MediaLibrary/target';
import { WikiTextEditor } from 'Shared/wiki_text_editor';
import EditorCache from 'Shared/prosemirror/editorcache';
import { CookieUtils } from 'Shared/cookies/cookie-utils';
import { getSerializedText } from 'Shared/prosemirror/tiptap/editor_cache';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { safeSessionStorage } from '@ifixit/utils';
import { StatusNotice } from 'Shared/status_notice';
import LegacyEditorHandle from 'Guide/legacy_editor_handle';
import { MultipleDocumentManager } from 'Guide/document_manager';
import guideMetaItemTemplate from 'ejs/guide_meta_item.ejs';
import guideMetaPrereqListTemplate from 'ejs/guide_meta_prereq_list.ejs';
import prereqListItemTemplate from 'ejs/prereq_list_item.ejs';
import Template from 'Shared/template';

onDomReady(() => {
   let handle = false;
   const elementAbovePM = document.querySelector('#partsItemEditor');

   const guideMeta = GuideMeta.initialize();

   if (elementAbovePM) {
      const showProseMirror = async function (entries) {
         const isInView = entries[0].target === elementAbovePM && entries[0].isIntersecting;

         if (isInView && !handle) {
            await getEditor();
            handle = true;
         }
      };
      const observer = new IntersectionObserver(showProseMirror);
      observer.observe(elementAbovePM);
   }

   async function getEditor() {
      // Teardowns don't have conclusions.
      if (!document.querySelector('#editConclusionTextBox')) {
         return;
      }

      const prosemirror = document.querySelector('.prosemirror');
      if (!prosemirror) {
         guideMetaLegacyHandle('editConclusionTextBox');
         return;
      }
      const { getOrCreateHandle } = await import(
         /* webpackPrefetch: true */ 'Shared/prosemirror/guide_intro.jsx'
      );
      return getOrCreateHandle(
         'editConclusionTextBox',
         '#conclusionDiv',
         guideMeta.handleConclusionModified.bind(guideMeta)
      );
   }

   if (window.TagEditor) {
      TagEditor.initialize();
   }
});

function guideMetaLegacyHandle(id) {
   EditorCache.set(id, LegacyEditorHandle('editConclusionTextBox'));
   // Set up the toolbar on the introduction box.
   const items = ['heading', 'bold', 'italic', 'ul', 'ol', 'link', 'video'];
   new WikiTextEditor(id, null, {
      toolbarItems: items,
      className: 'wikiTextEditorToolbar',
      helpLink: window.guide_constants.GuideURI('TAG_FORMATTING_HELP'),
      helpText: _js('Formatting help'),
      defaultOpenInNewTab: true,
   });
   EditorCache.get(id).editable.addClass('hasTip');
}

EditorCache.getOrCreate('editConclusionTextBox', () => LegacyEditorHandle('editConclusionTextBox'));

const GuideMeta = {
   managers: null,

   saved: false,

   initialize: function () {
      this.guideid = $('guideid').value;
      this.langid = $('langid').value;
      this.device = $('device').value;

      // Elements referenced below.
      this.form = $('guideIntroForm');
      this.formBody = $('formInputs');

      this.formModified = false;
      this.conclusionModified = false;

      // Each of the sections has a manager with a common interface for
      // checking if the section is modified and injecting inputs before
      // saving.
      this.managers = Object.filter(
         {
            form: this.initForm(this.form),
            prereqs: this.initPrereqs(),
            tools: this.initTools(),
            parts: this.initParts(),
            documents: this.initDocuments(),
         },
         o => o !== null
      );

      Object.each(
         this.managers,
         function (manager) {
            manager.addEvent('modifiedChanged', this.updateModifiedStatus.bind(this));
         },
         this
      );

      when($('mainGuideImage'), mainGuideImage => {
         MediaTarget.createFromElement(mainGuideImage);
      });

      this.updatePrereqItems(App.prereqItems);

      $E('.js-add-doc-button').addEvent('click', () => {
         this.managers.documents.browse();
      });

      $E('.document-form-field').addEvent(
         'click:relay(.feature-doc-button)',
         this.updateFeatureDocButtons.bind(this)
      );

      this.updateFeatureDocContainer();

      $('noJSFlag').set('value', 'false');

      return this;
   },

   /** FEATURE DOCUMENT *******************************************************/

   updateFeatureDocButtons: function (el) {
      let selectedDocClass = 'js-selected-feature-document';
      let selectedDoc = el.target.getParent().getParent();
      let oldFeaturedDoc = $E('.' + selectedDocClass);

      // Reset the formerly featured document, if any.
      if (oldFeaturedDoc) {
         oldFeaturedDoc.removeClass(selectedDocClass);
      }

      // Don't put the class back on if we're trying to un-feature it.
      if (selectedDoc !== oldFeaturedDoc) {
         selectedDoc.toggleClass(selectedDocClass);
      }

      this.updateFeatureDocContainer();
   },

   updateFeatureDocContainer: function () {
      let featuredDocument = $E('.js-selected-feature-document');
      let featuredContainer = $E('.js-feature-document');

      // Move the old document back into the normal list.
      $('attachedDocuments').adopt(featuredContainer.getChildren(), 'after');

      $$('.feature-doc-button').set('text', _js('Feature'));

      if (featuredDocument) {
         featuredContainer.grab(featuredDocument.dispose());
         featuredDocument.getElement('.feature-doc-button').set('text', _js("Don't Feature"));
      }
   },

   /** FORM MANAGER ***********************************************************/

   initForm: function (form) {
      this.timeRequiredMin = $('timeRequiredMin');
      this.timeRequiredMax = $('timeRequiredMax');
      this.difficultyInput = $('introDifficulty');
      this.conclusionInput = $('editConclusionTextBox');

      let formManager = new FormManager(form);

      // Submit.
      formManager.addSubmitHandler(this.submit.bind(this, form));

      // Unload warning.
      Utils.UnsavedChanges.addHandler(() => {
         if (!this.saved && this.formBody.hasClass('unsaved')) {
            return _js('You have unsaved changes. Your changes will be lost if you continue.');
         }
      });

      this.usingSliders = $('timeSlider');
      if (this.usingSliders) {
         this.initSliders();
      }

      return formManager;
   },

   initSliders: function () {
      let toggleElement = $('rangeToggle');
      let timeRequiredText = $$('.js-timeRequiredText')[0];

      let initialMinValue = this.timeRequiredMin.value.toInt();
      let initialMaxValue = this.timeRequiredMax.value.toInt();

      let minIndex = App.timeValues.indexOf(initialMinValue);
      let maxIndex = App.timeValues.indexOf(initialMaxValue);

      // Updates the slider's appearance, but doesn't set the changed values.
      let updateSliderText = function (firstValue, secondValue) {
         let from = App.timeValuesText[firstValue];
         let to = App.timeValuesText[secondValue];

         if (from == to) {
            timeRequiredText.set('text', from);
         } else if (firstValue === 0) {
            timeRequiredText.set('text', to);
         } else {
            let fromArr = from.split(' ');
            let toArr = to.split(' ');

            if (fromArr[1] == toArr[1]) {
               // Second word in the string matches. i.e. hours, minutes, etc.
               timeRequiredText.set('text', fromArr[0] + '-' + to);
            } else {
               timeRequiredText.set('text', from + ' - ' + to);
            }
         }
      };

      this.timeSlider = new RangeSlider($('timeSlider'), {
         type: minIndex == maxIndex ? 'single' : 'double',
         range: [0, App.timeValues.length - 1],
         position: [minIndex, maxIndex],
         onChange: updateSliderText,
         onComplete: function (firstValue, secondValue) {
            if (firstValue === 0 && secondValue !== 0) {
               this.timeRequiredMin.set('value', App.timeValues[secondValue]);
            } else {
               this.timeRequiredMin.set('value', App.timeValues[firstValue]);
            }

            this.timeRequiredMax.set('value', App.timeValues[secondValue]);
            this.timeRequiredMin.fireEvent('input');
            this.timeRequiredMax.fireEvent('input');
            updateSliderText(firstValue, secondValue);
         }.bind(this),
      });

      toggleElement.addEvent('click', () => {
         this.timeSlider.toggleType();
      });
   },

   /**
    * Before submitting we inject inputs, make sure the user is logged in,
    * and disable beforeunload warnings.
    */
   submit: function () {
      this.inject();
      Auth.required({
         from: $('saveBtn'),
         message: _js('Log in to save.'),
         onAuthorize: function () {
            this.saved = true;
            this.saveGuide();
         }.bind(this),
      });
   },

   saveGuide: function () {
      let guideValues = this.getFormValues();
      let path = 'guides/' + App.guideApi.guideid + '?revisionid=' + App.guideApi.revisionid;
      new Request.API_2_0(path, {
         method: 'PATCH',
         statusPanelMessage: _js('Saving...'),
         onSuccess: function (response) {
            CookieUtils.set('guide_updated', true, {
               maxAgeInSeconds: 2,
            });
            const firedEventsArray = safeSessionStorage.getJson('firedEvents') || [];
            if (
               safeSessionStorage.getItem('newGuideId') == response.guideid &&
               !firedEventsArray.includes('Details')
            ) {
               trackInPiwikAndGA({
                  eventCategory: 'Create a Guide Steps',
                  eventAction: 'Create a Guide Step - "Details" - Complete',
               });
               firedEventsArray.push('Details');
               safeSessionStorage.setJson('firedEvents', firedEventsArray);
            }
            window.location.reload();
         },
         onFailure: function (xhr) {
            const response = this.jsonParseSafely(xhr.response);
            if (response) {
               checkSpecialFailureCase(response);
            }
            this.saved = false;
         },
      }).send(guideValues);
   },

   getFormValues: function () {
      const getConclusion = () => getSerializedText('editConclusionTextBox');
      let imageInput = $('introImageid');
      let imageid = imageInput ? imageInput.value.toInt() || null : null;
      let values = {
         time_required_min: this.timeRequiredMin.value.toInt() || 0,
         time_required_max: this.timeRequiredMax.value.toInt() || 0,
         difficulty: this.difficultyInput.value,
         conclusion: getConclusion(),
         documents: this.getDocumentids(),
         featured_documentid: this.getFeaturedDocument(),
         image: imageid,
      };

      if (!this.usingSliders) {
         values['time_required_min'] *= 60;
         values['time_required_max'] *= 60;
      }

      // This covers prerequisites, parts, and tools.
      Object.each(this.managers, manager => {
         if (manager.getFieldName) {
            values[manager.getFieldName()] = manager.getValue();
         }
      });

      return values;
   },

   getDocumentids: function () {
      // Filter out non numeric values like the "Add" button.
      return $$('.docBody')
         .map(documentListItem => documentListItem.getElement('input').value.toInt())
         .filter(Number);
   },

   getFeaturedDocument: function () {
      let selectedDoc = $E('.js-selected-feature-document');
      return selectedDoc ? selectedDoc.getElement('input').value.toInt() : null;
   },

   /** PREREQS ***************************************************************/

   initPrereqs: function () {
      let prereqsDiv = $('prereqsDiv');
      if (!prereqsDiv) {
         return null;
      }
      window.prereqEditor = new PrereqEditor($('prereqsDiv'));
      prereqEditor.addEvent('changed', this.fetchPrereqItems.bind(this));
      return prereqEditor;
   },

   /** TOOLS *****************************************************************/

   initTools: function () {
      let toolEditor = $('toolsItemEditor');
      if (!toolEditor) {
         return null;
      }
      return new ItemEditor('tool', toolEditor, {
         sortable: true,
         clickable: true,
         blurbFinder: {
            langid: this.langid,
            fallbackToDefaultLanguage: true,
            postData: { promptNew: 'tool' },
         },
      });
   },

   /** PARTS *****************************************************************/

   initParts: function () {
      let partEditor = $('partsItemEditor');
      if (!partEditor) {
         return null;
      }
      return new ItemEditor('part', partEditor, {
         sortable: true,
         clickable: true,
         blurbFinder: {
            langid: this.langid,
            fallbackToDefaultLanguage: true,
            postData: { promptNew: 'part' },
         },
      });
   },

   /** DOCUMENTS *************************************************************/

   initDocuments: function () {
      return new MultipleDocumentManager($('attachedDocuments'));
   },

   /** MODIFIED STATUS *******************************************************/

   updateModifiedStatus: function (formModified) {
      this.formModified = formModified;
      const modified = formModified || this.conclusionModified;

      this.formBody[modified ? 'addClass' : 'removeClass']('unsaved');
      $('saveBtn').toggleClass('buttonLinkDisabled', !modified);
      $('saveBtn').disabled = !modified;
   },

   handleConclusionModified: function (modified) {
      this.conclusionModified = modified;
      this.updateModifiedStatus(this.formModified);
   },

   /** SUBMITTING ************************************************************/

   inject: function () {
      // Clear out any existing injected inputs.
      $$('.injected').dispose();

      // Call the inject method of each manager that has it.
      Object.each(this.managers, manager => {
         if (manager.inject) {
            manager.inject();
         }
      });
   },

   /**
    * Creates a hidden input to be inserted into the intro form,
    * parameterized by a type (e.g. 'tool'), a value, and one or more
    * indices (to create an arbitrarily nested HTML array). This function is
    * used by the injectX functions that follow to inject inputs
    * corresponding to prereqs, tools, etc. added by the user.
    */
   makeInput: function (type, value, indices) {
      // Makes something like intro[prereqs][0][prereq_guideid].
      indices = [type + 's'].concat(Array.convert(arguments).slice(2));
      let name = 'intro' + indices.map(index => '[' + index + ']').join('');
      // Create the input and return it.
      return new Element('input', {
         type: 'hidden',
         name: name,
         value: value,
      }).addClass('injected');
   },

   fetchPrereqItems: function (prereqData) {
      let guideids = prereqData.map(prereq => prereq.guideid);
      new Request.AjaxIO('getPrereqItems', {
         onSuccess: this.updatePrereqItems.bind(this),
      }).send(guideids, this.langid);
   },

   updatePrereqItems: function (items) {
      if (items.tools && this.managers.tools) {
         this.managers.tools.registerPrereqItems(items.tools);
      }
      if (items.parts && this.managers.parts) {
         this.managers.parts.registerPrereqItems(items.parts);
      }
   },
};

function checkSpecialFailureCase(response) {
   const response_code = response.errors[0].code;
   const guideid = response.errors[0].index;
   if (response_code === 'circular_prereq' && guideid) {
      const prereq_error_message = _js(
         'Circular prerequisite references not allowed. Remove the following prerequisite guide or its circular reference back to the current guide to continue. Guide ID: %1',
         guideid
      );
      displayPersistentError(prereq_error_message);
   }
}

function displayPersistentError(message) {
   const statusNoticeDiv = $('statusNoticeDiv');
   const notice = new StatusNotice();
   statusNoticeDiv.adopt(notice.element);
   notice.error(message);
   statusNoticeDiv.jumpTo();
}

// PREREQ EDITOR //////////////////////////////////////////////////////////////

const PrereqEditor = new Class({
   Implements: Events,

   container: null,
   list: null,
   input: null,
   prereqTemplate: null,

   originalValues: [],
   prereqData: [],
   modified: false,

   sortables: null,

   initialize: function (div) {
      this.container = $(div);

      this.list = this.container.getElement('#introPrereqs');
      this.input = this.container.getElement('#introPrereqInput');

      // Store the first (empty) prereq item as a template to make new ones.
      let prereqs = this.list.getElements('li');
      this.prereqTemplate = prereqs.shift().dispose();
      prereqs.each(li => {
         let prereqData = {
            guideid: li.getProperty('data-guideid'),
         };
         this.originalValues.push(prereqData);
         li.store('PrereqEditor:prereqData', prereqData);

         // Add events.
         this.addPrereqEvents(li);
      });
      this.prereqData = this.originalValues.clone();

      // Allow prereqs to be re-arranged.
      this.sortables = new Sortables(this.list, {
         handle: '.prereqBody',
         onSort: this.updateModifiedStatus.bind(this),
      });

      new SuggestDeviceGuides(this.input, {
         device: GuideMeta.device,
         langid: App.sourceLangid,
         inProgressOkay: true,
         prereqOnlyOkay: true,
         overflow: true,
         getValue: this.blurbFinderGetValue.bind(this),
         onSelection: this.addSelectedPrereq.bind(this),
         submitOnEnter: false,
      });
   },

   blurbFinderGetValue: function (blurb) {
      return blurb.title;
   },

   addSelectedPrereq: function (choice, finish) {
      if (!finish) {
         return;
      }

      let guide = choice.retrieve('blurb');
      this.input.blur();

      window.prereqEditor = this;
      this.getPrereqPrereqs(guide.guideid).done(prereqs => {
         prereqEditor.showUserPrereqList(guide, prereqs, selectedPrereqs => {
            prereqEditor.addNewPrereqs(selectedPrereqs);
            prereqEditor.addNewPrereq(guide);
            prereqEditor.fireEvent('changed', [prereqEditor.prereqData]);
            prereqEditor.updateModifiedStatus();
         });
      });

      this.input.set('value', '').focus();
   },

   /**
    * When a user attaches a guide as a prereq, this shows the user a list
    * of that guide's prereqs, and asks them if they want to attach these
    * as well.
    */
   showUserPrereqList: function (guide, prereqs, callback) {
      let alreadyAdded = prereqs.filter(prereq => !prereqEditor.isNewPrereq(prereq.guideid));

      if (prereqs.length === 0) {
         return callback([]);
      }

      let newPrereqs = prereqs.filter(prereq => prereqEditor.isNewPrereq(prereq.guideid));

      let template = Template.get(guideMetaPrereqListTemplate);

      let prereqList = template({
         guide: guide,
         alreadyAdded: alreadyAdded,
         newPrereqs: newPrereqs,
      });

      // By default the content of the modal is hidden, but not removed
      // from the DOM when the modal is closed.
      Modal.addEvents({
         onElementUnload: function (modal, content) {
            content.destroy();
         },
      });

      Modal.open({
         type: 'element',
         element: prereqList,
         onCancel: function () {
            // If the user exits the modal, don't attach any of the prereq's
            // prereqs.
            callback([]);
         },
      });

      let prereqsByGuideid = {};
      prereqs.forEach(prereq => {
         prereqsByGuideid[prereq.guideid] = prereq;
      });

      // Removes a prereq from the "Already Added" list and puts it into the
      // "Adding" list.
      $$('.re-add-prereq').addEvent('click', function () {
         let guideid = this.getProperty('data-guideid');
         let prereq = prereqsByGuideid[guideid];

         if (!prereq) {
            return;
         }

         let prereqTemplate = Template.get(prereqListItemTemplate);
         let prereqItem = prereqTemplate(prereq);

         this.getParent('.prereq-list-item').dispose();
         $$('.to-be-added').grab(prereqItem, 'top');
      });

      // Canceling the modal is the same as selecting no prereqs.
      $$('.js-cancel-prereqs').addEvent('click', () => {
         callback([]);
         Modal.close();
      });

      // Grab the prereqs the user selected.
      $$('.js-add-prereqs').addEvent('click', () => {
         let selected = $$('#prereq-list input[type=checkbox]:checked');
         let selectedIdStrs = selected.getProperty('data-guideid');
         let selectedIds = selectedIdStrs.map(idStr => Number.parseInt(idStr, 10));

         let selectedPrereqs = selectedIds.map(guideid => prereqsByGuideid[guideid]);

         callback(selectedPrereqs);
         Modal.close();
      });
   },

   addNewPrereqs: function (prereqs) {
      prereqs.forEach(prereq => {
         prereqEditor.addNewPrereq(prereq);
      });
   },

   addNewPrereq: function (prereq) {
      let li = this.prereqTemplate.clone();
      li.getElement('.prereqName').set('text', prereq.title);
      li.setProperty('data-url', prereq.url);

      let data = {
         guideid: '' + prereq.guideid,
      };
      this.prereqData.push(data);

      // Store the prereq's identifying data.
      li.store('PrereqEditor:prereqData', data);

      // Add events.
      this.addPrereqEvents(li);
      this.sortables.addItems(li);

      // Update the interface.
      li.inject(this.list).show();
   },

   addPrereqEvents: function (li) {
      li.addEvents({
         mouseenter: li.addClass.bind(li, 'active'),
         mouseleave: li.removeClass.bind(li, 'active'),
      });

      let url = li.get('data-url');
      url &&
         li.addEvent('click', () => {
            window.open(url, '_blank');
         });

      li.getElement('.delete').addEvent('click', this.deletePrereq.bind(this, li));
   },

   isNewPrereq: function (guideid) {
      return !this.list.getElements('li').some(li => {
         let data = li.retrieve('PrereqEditor:prereqData');
         return data.guideid == guideid;
      });
   },

   deletePrereq: function (li, ev) {
      ev.stop();

      let guideid = li.retrieve('PrereqEditor:prereqData').guideid;
      li.dispose();

      // Remove this guide from the list of guide values.
      for (let i = 0; i < this.prereqData.length; i++) {
         if (this.prereqData[i].guideid == guideid) {
            this.prereqData.splice(i, 1);
            break;
         }
      }

      this.fireEvent('changed', [this.prereqData]);
      this.updateModifiedStatus();
   },

   /**
    * Compares each current <li>'s identifying info against the original
    * <li>'s info and updates the modified status if there are any differences
    */
   updateModifiedStatus: function () {
      let modified = false;
      let prevNumPrereqs = this.originalValues.length;
      let currPrereqs = this.list.getElements('li');

      modified =
         prevNumPrereqs == currPrereqs.length
            ? currPrereqs.some((li, index) => {
                 let orig = this.originalValues[index];
                 let curr = li.retrieve('PrereqEditor:prereqData');
                 return orig.guideid != curr.guideid;
              })
            : true;

      if (modified != this.modified) {
         this.fireEvent('modifiedChanged', modified);
         this.modified = modified;
      }
   },

   /**
    * Injects form fields for prerequisites; assumes that any
    * previously-created inputs have already been disposed of.
    */
   inject: function () {
      if (!this.container) {
         return;
      }

      this.list.getElements('li').each((li, index) => {
         let data = li.retrieve('PrereqEditor:prereqData');
         GuideMeta.makeInput('prereq', data.guideid, index + 1, 'prereq_guideid').inject(li);
         GuideMeta.makeInput('prereq', data.langid, index + 1, 'prereq_langid').inject(li);
      });
   },

   getFieldName: function () {
      return 'prerequisites';
   },

   getValue: function () {
      return this.list.getElements('li').map(li => li.retrieve('PrereqEditor:prereqData').guideid);
   },

   getPrereqPrereqs: function (guideid) {
      let path = 'guides/' + guideid;

      let response = new Request.API_2_0(path, {
         method: 'GET',
         data: {
            excludePrerequisiteSteps: 1,
            langid: App.sourceLangid,
         },
      }).send();

      return response.then(
         guideInfo => guideInfo.prerequisites,
         () => []
      );
   },
});

// ITEM EDITOR ////////////////////////////////////////////////////////////////

const ItemEditor = new Class({
   Implements: [Options, Events],

   options: {
      sortable: false,
      clickable: false,

      blurbFinder: {
         relative: false,
         submitOnEnter: false,
         overflow: true,
         itemType: 'item',
      },
   },

   // Elements
   container: null,
   itemEditForm: null,
   itemTemplate: null,
   cancelEditingLink: null,
   doneEditingLink: null,
   newItemForm: null,
   importItemsBlock: null,
   itemActionsBlock: null,
   newItemBlock: null,
   clearItemsBlock: null,

   // Template to render item text HTML from a data object.
   itemTextTemplate: Template.get(guideMetaItemTemplate),

   // A list of original values for the items.
   originalValues: [],

   // A list of items on prerequisite guides (used for import).
   prereqItems: [],

   // A sortables instance, if the item list is sortable.
   sortables: null,

   // Whether or not the item list has been modified.
   modified: false,

   // These are the possible properties of an object representing an item and
   // also the unique parts of class names used to store the same information
   // in the DOM.
   itemTextKeys: ['quantity', 'name', 'type', 'notes', 'summary', 'url', 'isoptional'],

   initialize: function (itemType, form, options) {
      this.itemType = itemType;
      this.container = form;
      this.itemEditForm = form.getElement('.itemEditForm');

      options.blurbFinder.itemType = this.itemType;
      this.setOptions(options);

      this.itemActionsBlock = form.getElement('.itemActions');

      // To import items from prerequisites.
      this.importItemsBlock = form.getElement('.importItems');
      this.importItemsBlock.getElement('a').addEvent('click', this.importPrereqItems.bind(this));

      // To begin editing an item.
      this.newItemBlock = this.itemActionsBlock.getElement('.addNewItem');
      this.newItemBlock.addEvent('click', this.beginEdit.bind(this, null));

      // To finish.
      this.doneEditingLink = form.getElement('.doneEditingLink');
      this.doneEditingLink.addEvent('click', this.finishEdit.bind(this));

      this.cancelEditingLink = form.getElement('.cancelEditingLink');
      this.cancelEditingLink.addEvent('click', this.cancelEdit.bind(this));

      // To create a new item.
      this.newItemForm = form.getElement('.newItemForm');
      this.newItemForm.getElement('button').addEvent('click', this.finishNewItem.bind(this));

      // To toggle the optional fields.
      this.toggleOptionalLink = form.getElement('.toggleOptionalLink');
      this.toggleOptionalLink.addEvent('click', this.toggleOptional.bind(this));

      // To clear all items.
      this.clearItemsBlock = this.itemActionsBlock.getElement('.clearItems');
      this.clearItemsBlock.addEvent('click', this.clearItems.bind(this));

      // Grab the new item template and collect the pre-existing items.
      let items = this.getItems();
      this.itemTemplate = items.shift().dispose();
      items.each(li => {
         this.storeItemData(li);
         this.originalValues.push(this.retrieveItemData(li));
         this.addItemEvents(li);
      });

      let nameInput = this.itemEditForm.getElement('.itemEditName');
      let suggestOptions = this.options.blurbFinder;
      if (!suggestOptions.getValue) {
         suggestOptions.getValue = this.getBlurbFinderValue.bind(this);
      }
      if (!suggestOptions.onSelection) {
         suggestOptions.onSelection = this.onBlurbFinderSelection.bind(this);
      }
      new SuggestItems(nameInput, suggestOptions);

      if (this.options.sortable) {
         this.sortables = new Sortables(this.container.getElement('ul'), {
            handle: '.itemBody',
            onSort: this.finishReorder.bind(this),
         });
      }
   },

   registerPrereqItems: function (items) {
      this.prereqItems = items;
      this.updatePrereqImportLink();
   },

   importPrereqItems: function (ev) {
      ev.stop();

      let importItems = this.getMissingPrereqItems();
      if (!importItems) {
         return;
      }

      // Reverse the list of prereq items since we're going to insert them
      // each at the top of the list.
      let items = this.container.getElement('ul');
      importItems.reverse().forEach(function (data) {
         let newItem = this.itemTemplate.clone();
         let itemTextHtml = this.itemTextTemplate(
            {
               // The template requires these be present.
               url: '',
               summary: '',
               ...data,
            },
            /* asString */ true
         );
         newItem.getElement('.itemText').set('html', itemTextHtml);
         // To view the associated item page by clicking on the item.
         newItem.setProperty('data-url', this.itemTemplate.get('data-url') + data.wikititle);
         if (this.isNewItem(newItem)) {
            newItem.inject(items, 'top').show();
            this.storeItemData(newItem);
            this.addItemEvents(newItem);
         }
      }, this);

      this.itemsModified();
   },

   updatePrereqImportLink: function () {
      let numItems = this.getMissingPrereqItems().length;
      if (numItems > 0) {
         let msgEl = this.importItemsBlock.getElement('.importItemsMsg');
         let msg =
            numItems > 1
               ? msgEl.getProperty('data-plural').substitute({ count: numItems })
               : msgEl.getProperty('data-singular');
         msgEl.set('text', msg);
         this.importItemsBlock.show();
      } else {
         this.importItemsBlock.hide();
      }
   },

   /**
    * Performs a set intersection between the prerequisite items and the
    * current items by the item name (i.e., the item's wiki title).
    */
   getMissingPrereqItems: function () {
      let attachedItems = {};
      this.getItems().each(li => {
         attachedItems[li.getElement('.itemName').get('text')] = true;
      });
      return this.prereqItems.filter(item => attachedItems[item.name] === undefined);
   },

   beginEdit: function (editItem, ev) {
      ev.stop();

      // You can only edit one thing at a time.
      if (this.itemEditForm.visible()) {
         this.finishEdit();
      }

      if (editItem) {
         this.itemEditForm.getElement('.doneEditingLink').removeProperty('disabled');
      } else {
         // We're adding a new item, so we display the itemEditForm in a new li
         // at the bottom of the list.
         let items = this.container.getElement('ul');
         editItem = this.itemTemplate.clone().inject(items).show();
         this.addItemEvents(editItem);
         this.storeItemData(editItem);
      }

      editItem.addClass('editing');

      // Insert the edit div into the list item, and fill its fields from the
      // stored item data (if there is any).
      let oldContent = editItem.getElement('.itemBody');
      editItem.store('ItemEditor:oldContent', oldContent);
      this.itemEditForm.replaces(oldContent).show();
      let hasDetails = this.fillEditFormFromItem(editItem);
      let lastName = this.itemEditForm.getElement('.itemEditName').value;
      editItem.store('ItemEditor:lastName', lastName);
      if (hasDetails) {
         this.openOptional();
      } else {
         this.toggleOptionalLink.show();
      }

      this.itemActionsBlock.hide();
      this.itemEditForm.getElement('.itemEditName').select();
   },

   finishEdit: function (ev) {
      if (ev) {
         ev.stop();
      }

      let li = this.itemEditForm.getParent('li');

      let editValid = false;

      // Do we at least have an item name?
      if (li.getElement('.itemEditName').value) {
         let data = this.buildItemDataFromEditForm();
         data.name = li.retrieve('ItemEditor:lastName');
         let itemTextHtml = this.itemTextTemplate(data, /* asString */ true);
         let oldContent = li.retrieve('ItemEditor:oldContent');
         oldContent.inject(this.itemEditForm, 'before');
         oldContent.getElement('.itemText').set('html', itemTextHtml);

         this.storeItemData(li);
         editValid = this.isNewItem(li);
      }
      this.cleanUpEdit(li, editValid);
   },

   cancelEdit: function (ev) {
      if (ev) {
         ev.stop();
      }

      let li = this.itemEditForm.getParent('li');
      let isEdit = false;

      // Do we at least have an item name?
      if (li.getElement('.itemEditName').value) {
         let oldContent = li.retrieve('ItemEditor:oldContent');
         isEdit = !!oldContent.getElement('.itemName');
         if (isEdit) {
            oldContent.inject(this.itemEditForm, 'before');
         }

         this.storeItemData(li);
      }
      this.cleanUpEdit(li, isEdit);
   },

   cleanUpEdit: function (li, editValid) {
      li.removeClass('editing');

      // Restore the item edit form to a clean state.
      this.itemEditForm.getElements('input[type="text"], textarea').each(input => {
         input.value = '';
      });
      this.itemEditForm.getElement('input[type="checkbox"]').checked = false;
      this.itemEditForm.getElement('.doneEditingLink').setProperty('disabled', 'disabled');
      this.closeOptional();
      this.itemEditForm.dispose();

      this.itemActionsBlock.show();

      if (editValid) {
         this.itemsModified();
      } else {
         if (this.sortables) {
            this.sortables.removeItems(li);
         }
         li.dispose();
      }
   },

   finishReorder: function () {
      this.updateModifiedStatus();
   },

   storeItemData: function (li) {
      let data = {};
      let fields = li.getElements('.itemText span');
      fields.each(span => {
         let key = span.get('class').replace(/^item/, '').toLowerCase();
         data[key] = span.get('text');
      });

      const nameSpan = li.getElement('.itemName');
      data.wikititle = nameSpan && nameSpan.getProperty('data-wikititle');

      data.isoptional =
         // Set by checkbox
         li.getElement('.itemEditIsOptional')?.checked ||
         nameSpan?.dataset?.isoptional === 'true' ||
         // Set by server
         false;

      li.store('ItemEditor:itemData', data);
      return data;
   },

   retrieveItemData: function (li) {
      return li.retrieve('ItemEditor:itemData');
   },

   fillEditFormFromItem: function (li) {
      let hasDetails = false;
      let data = this.retrieveItemData(li);

      const wikititle = data.wikititle;
      delete data.wikititle;
      // Fill in any pre-existing values.
      Object.each(
         data,
         function (value, key) {
            let className = '.itemEdit' + key.capitalize();
            let field = this.itemEditForm.getElement(className);
            field.type === 'checkbox'
               ? (field.checked = Boolean(value))
               : field.set('value', value);

            // If the field is non-empty, not the item name field, and
            // not a quantity of 1, then this item has details.
            hasDetails |= value != '' && key != 'name' && (key != 'quantity' || value != 1);
         },
         this
      );

      this.itemEditForm.getElement('.itemEditName').setProperty('data-wikititle', wikititle);
      this.itemEditForm
         .getElement('.itemEditName')
         .setProperty('data-isoptional', data.isoptional ? 'true' : 'false');
      return hasDetails;
   },

   buildItemDataFromEditForm: function () {
      let data = {};

      this.itemTextKeys.forEach(function (key) {
         const el = this.itemEditForm.getElement('.itemEdit' + key.capitalize());
         let value = el.type === 'checkbox' ? el.checked : el.value.trim();

         // Default the quantity to 1 if it's absent or invalid.
         if (key == 'quantity') {
            value = value.toFloat();
            if (isNaN(value) || value <= 0) {
               value = 1;
            }
         }

         data[key] = value;
      }, this);

      // Extract wikititle because it isn't stored like other data
      let name = this.itemEditForm.getElement('.itemEditName').getProperty('data-wikititle');
      data.wikititle = name;

      return data;
   },

   toggleOptional: function (ev) {
      ev.stop();
      this.itemEditForm.getElement('.optionalFields').toggle();
      this.toggleOptionalLink.hide();
      let typeField = this.itemEditForm.getElement('.itemEditType');
      if (typeField.visible()) {
         typeField.select();
      }
   },

   openOptional: function () {
      this.itemEditForm.getElement('.optionalFields').show();
      this.toggleOptionalLink.hide();
      this.itemEditForm.getElement('.itemEditType').select();
   },

   closeOptional: function () {
      this.itemEditForm.getElement('.optionalFields').hide();
   },

   getBlurbFinderValue: function (blurb) {
      // the new Items display title is add new item "<item name>"
      // and we dont want that to the items name in the itemEditName field
      return blurb.newItem ? blurb.title : blurb.display_title;
   },

   onBlurbFinderSelection: function (choice, finish) {
      if (!finish) {
         return;
      }

      // Focus the next input, which causes the currently-highlighted item to
      // be dropped into the input, and the autocompleter to go away.
      this.itemEditForm.getElement('.itemEditType').select();
      let blurb = choice.retrieve('blurb');
      // lastName we check for legitimate changes to display name
      let li = this.itemEditForm.getParent('li');
      const name = blurb.newItem ? blurb.title : blurb.display_title;
      li.store('ItemEditor:lastName', name);

      this.itemEditForm.getElement('.itemEditName').setProperty('data-wikititle', blurb.title);
      this.itemEditForm.getElement('.doneEditingLink').removeProperty('disabled');
      if (blurb.newItem) {
         this.beginNewItem(blurb);
      }
   },

   /**
    * Triggered when there are no suggestions for a user-entered item, and the
    * user clicks on the "Add new..." option.
    */
   beginNewItem: function (blurb) {
      this.newItemForm.getElement('textarea').set('value', '');
      this.newItemForm.getElement('input').set('value', '');
      this.newItemForm.getElement('.itemName').set('text', '"' + blurb.title + '"');
      this.newItemForm.getElement('.itemName').setProperty('data-wikititle', blurb.title);
      this.newItemForm.getElement('.itemName').setProperty('data-isoptional', 'false');

      Modal.open({
         type: 'element',
         element: this.newItemForm,
      });
   },

   /**
    * Triggered when the user clicks on the "Save" button in the "new item"
    * modal. Fills in the item's details from the modal form. These will be
    * extracted into element storage once the user completes editing the item
    * (the user has a chance to edit the item normally first).
    */
   finishNewItem: function (ev) {
      ev.stop();

      let summary = this.newItemForm.getElement('textarea').get('value');
      let url = this.newItemForm.getElement('input').get('value');
      this.itemEditForm.getElement('.itemEditSummary').set('value', summary);
      this.itemEditForm.getElement('.itemEditUrl').set('value', url);

      Modal.close();
   },

   updateModifiedStatus: function () {
      let modified = false;
      let currents = this.getUnalteredValue();

      modified =
         currents.length == this.originalValues.length
            ? currents.some(function (data, index) {
                 const original = this.originalValues[index];
                 return Object.some(data, (value, key) => value != original[key]);
              }, this)
            : true;

      if (modified != this.modified) {
         this.fireEvent('modifiedChanged', modified);
         this.modified = modified;
      }
   },

   /**
    * Called when we either add or remove items.
    */
   itemsModified: function () {
      this.updatePrereqImportLink();
      this.updateClearItemsLink();
      this.updateModifiedStatus();
   },

   addItemEvents: function (li) {
      let listItemData = li.retrieve('ItemEditor:itemData');

      if (listItemData) {
         li.addEvents({
            mouseenter: li.addClass.bind(li, 'active'),
            mouseleave: li.removeClass.bind(li, 'active'),
         });
      }

      li.getElement('.delete').addEvent('click', this.clearItem.bind(this, li));

      li.getElement('.edit').addEvent('click', this.beginEdit.bind(this, li));

      if (this.sortables) {
         this.sortables.addItems(li);
      }

      if (this.options.clickable && listItemData) {
         if (this.modified) {
            li.set('data-url', this.itemTemplate.get('data-url') + listItemData.wikititle);
         }
         li.getElement('.itemBody').addEvent('click', ev => {
            ev.stop();
            window.open(li.get('data-url'), '_blank');
         });
      }
   },

   updateClearItemsLink: function () {
      this.clearItemsBlock.toggle(this.getItems().length > 0);
   },

   clearItems: function (ev) {
      ev.stop();
      this.deleteItems(this.getItems());
   },

   clearItem: function (li, ev) {
      ev.stop();
      this.deleteItems([li]);
   },

   deleteItems: function (items) {
      if (this.sortables) {
         this.sortables.removeItems(items);
      }
      items.invoke('dispose');
      this.itemsModified();
   },

   /**
    * An item <li> is new if there isn't some <li> in this ItemEditor's
    * collection (which isn't the passed-in <li> itself) that has the same
    * name.
    */
   isNewItem: function (newLi) {
      let newName = newLi.getElement('.itemName').get('text');
      const newWikiTitle = newLi.getElement('.itemName').getProperty('data-wikititle');
      return !this.getItems().some(li => {
         const itemNameElement = li.getElement('.itemName');
         return (
            li !== newLi &&
            (itemNameElement.get('text') == newName ||
               itemNameElement.getProperty('data-wikititle') == newWikiTitle)
         );
      });
   },

   getItems: function () {
      return this.container.getElements('.listItem:not(.editing)');
   },

   inject: function () {
      if (this.itemEditForm.visible()) {
         this.finishEdit();
      }

      this.getItems().each(function (li, index) {
         let itemBody = li.getElement('.itemBody');
         let data = this.retrieveItemData(li);

         Object.each(
            data,
            function (value, key) {
               GuideMeta.makeInput(this.itemType, value, index, key).inject(itemBody, 'after');
            },
            this
         );
      }, this);
   },

   getFieldName: function () {
      // Either parts or tools.
      return this.itemType + 's';
   },

   getValue: function () {
      return this.getUnalteredValue()
         .filter(data => data.wikititle)
         .map(data => {
            data.name = data.wikititle;
            delete data.wikititle;
            return data;
         });
   },

   getUnalteredValue: function () {
      return this.getItems().map(this.retrieveItemData);
   },
});
